import React, { JSX, useEffect, useState, useRef } from "react";
import { LuPanelLeftClose, LuPanelLeft } from "react-icons/lu";
import { MdDashboard, MdOutlineCases } from "react-icons/md";
import { IoChevronDownOutline } from "react-icons/io5";
import { FaSort, FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa";
import { IoAddCircleOutline } from "react-icons/io5";


import { NavLink, useParams, useNavigate } from "react-router";
import { useProjectsStore, useProjectsPage } from "../state/projects";
import { useModal } from "../context/ModalContext";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { BsGrid, BsBuilding } from 'react-icons/bs';
import { AiOutlinePlus } from 'react-icons/ai';
import { format } from 'date-fns';
import { getVersion } from "../api/version";
import { getBadgeTailwindClasses } from '../styles/designSystem';
import { NewProjectForm } from './NewProjectForm';

type NavInterface = {
  id?: string;
  link?: string;
  label: string;
  icon?: JSX.Element;
  children?: NavInterface[];
  type?: 'string';
  issueCount?: number;
  taskCount?: number;
  projectCount?: number;
}

const NAV:NavInterface[] = [
  {
    link: '/map',
    label: 'Dashboard',
    icon: <MdDashboard />
  },
  {
    link: '/projects',
    label: 'Projects',
    icon: <MdOutlineCases />
  },
];

const getRandomIssueCount = () => Math.floor(Math.random() * 6);

// Function to count total tasks from all projects
const getTotalTasksCount = (projects: ProjectInterface[]) => {
  let totalTasks = 0;
  
  projects.forEach((project: ProjectInterface) => {
    // Count Project Plan items
    if (project.items) {
      totalTasks += project.items.length;
    }

    // Count Action Items
    if ((project as any).actionItems) {
      totalTasks += (project as any).actionItems.length;
    }
  });

  return totalTasks;
};

// Function to determine project color based on new rules (same as in DashboardList)
const getProjectColor = (project: ProjectInterface): string => {
  // Check if project has no Action Items or Project Plan Items
  const hasNoItems = (!project.items || project.items.length === 0) && 
                     (!(project as any).actionItems || (project as any).actionItems.length === 0);
  
  if (hasNoItems) {
    return 'bg-green-500'; // Green if no items
  }
  
  // Check if all tasks have no risk or low risk
  const allItemsLowRisk = project.items?.every(item => 
    !item.risks || item.risks === null || item.risks.toLowerCase() === 'low' || item.risks.toLowerCase() === 'none'
  ) ?? true;
  
  const allActionItemsLowRisk = (project as any).actionItems?.every((item: any) => 
    !item.priority || item.priority === null || item.priority.toLowerCase() === 'low'
  ) ?? true;
  
  if (allItemsLowRisk && allActionItemsLowRisk) {
    return 'bg-green-500'; // Green if all risks are low or none
  }
  
  // Fall back to original status-based logic
  if (project.status === 'NOT_STARTED') return 'bg-gray-500';
  if (project.status === 'COMPLETED') return 'bg-orange-400';
  if (project.status === 'IN_PROGRESS') return 'bg-red-500';
  
  return 'bg-gray-500'; // Default
};

const getIssueColor = (count: number): string => {
  if (count >= 4) return 'bg-red-500';
  if (count >= 2) return 'bg-orange-400';
  if (count >= 1) return 'bg-yellow-400';
  return 'bg-green-500';
};

const extendNavWithProjects = (baseNav: NavInterface[], projects: ProjectInterface[], currentProject: string, sortBy: string = 'name') => {
  const tasksCount = getTotalTasksCount(projects);
  
  // Sort projects based on sortBy parameter
  const sortedProjects = [...projects].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'name_desc':
        return b.name.localeCompare(a.name);
      case 'status':
        return (a.status || '').localeCompare(b.status || '');
      case 'created_date':
        return new Date(b.start_date || '').getTime() - new Date(a.start_date || '').getTime();
      case 'created_date_desc':
        return new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime();
      default:
        return a.name.localeCompare(b.name);
    }
  });
  
  return baseNav.map(navItem => {
    if (navItem.label === 'Tasks') {
      return {
        ...navItem,
        taskCount: tasksCount
      };
    }
    
    if (navItem.label === 'Projects') {
      return {
        ...navItem,
        projectCount: projects.length,
        children: sortedProjects.map(project => {
          // Get the project color class and convert it to issue count for existing logic
          const colorClass = getProjectColor(project);
          let issueCount = 0;
          if (colorClass === 'bg-green-500') issueCount = 0;
          else if (colorClass === 'bg-gray-500') issueCount = 1;
          else if (colorClass === 'bg-orange-400') issueCount = 2;
          else if (colorClass === 'bg-red-500') issueCount = 4;
          
          // Create rooms navigation directly from project.rooms
          const flatRooms: NavInterface[] = [];
          if (project.id === currentProject && project.rooms) {
            project.rooms.forEach(room => {
              flatRooms.push({
                link: `/projects/${currentProject}/sites/${room.site_id}/floors/${room.floor_id}/rooms/${room.id}`,
                label: room.name,
                id: room.site_id,
                issueCount: getRandomIssueCount()
              });
            });
          }
          
          return {
            label: project.name,
            link: `/projects/${project.id}`,
            issueCount: issueCount,
            ...(project.id === currentProject ? {children: flatRooms} : {}),
          };
        })
      }
    }

    return navItem;
  })
}

export const DashboardMenu = () => {
  const [collapsed, setCollapsed] = useState(localStorage.getItem('collapsed') === 'true');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showProjectSortDropdown, setShowProjectSortDropdown] = useState(false);
  const [projectSortBy, setProjectSortBy] = useState<string>('name');
  const [versionInfo, setVersionInfo] = useState({ build: '825', build_date: '', version: '' });
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const projectSortRef = useRef<HTMLDivElement>(null);
  const projects = useProjectsStore((state) => state.projects);
  const { projectId } = useParams();
  const { setShowCreateRoomModal, setCreateRoomModalProps } = useModal();
  const { loadProjectsPage } = useProjectsPage();
  const { isFeatureEnabled } = useFeatureFlag();
  const navigate = useNavigate();

  // Get siteId for current project
  const currentSiteId = projects.find(p => p.id === projectId)?.sites?.[0]?.site_id || '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
      }
      if (projectSortRef.current && !projectSortRef.current.contains(event.target as Node)) {
        setShowProjectSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load version information from API
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const version = await getVersion();
        setVersionInfo({
          build: version.build || '825',
          build_date: version.build_date || '',
          version: version.version || ''
        });
      } catch (error) {
        console.error('Failed to load version:', error);
        // Keep default fallback values
      }
    };

    loadVersion();
  }, []);

  // Listen for room creation events to refresh projects data
  useEffect(() => {
    const handleRoomCreated = () => {
      console.log('🏠 Room created event received in DashboardMenu, refreshing projects...');
      loadProjectsPage(1).catch((error: any) => {
        console.error('❌ Failed to reload projects after room creation:', error);
      });
    };

    window.addEventListener('roomCreated', handleRoomCreated);
    
    return () => {
      window.removeEventListener('roomCreated', handleRoomCreated);
    };
  }, [loadProjectsPage]);

  const extendedNav = extendNavWithProjects(NAV, projects, projectId || '', projectSortBy);

  // Sort options for projects
  const projectSortOptions = [
    { value: 'name', label: 'Name (A-Z)', icon: <FaSortAlphaDown /> },
    { value: 'name_desc', label: 'Name (Z-A)', icon: <FaSortAlphaUp /> },
    { value: 'status', label: 'Status', icon: <FaSort /> },
    { value: 'created_date', label: 'Created Date (Newest)', icon: <FaSort /> },
    { value: 'created_date_desc', label: 'Created Date (Oldest)', icon: <FaSort /> },
  ];

  const currentSortOption = projectSortOptions.find(option => option.value === projectSortBy) || projectSortOptions[0];
  
  return(
    <>
    <aside className={`dashboard-menu bg-white border-r border-gray-100 max-h-screen overflow-y-auto transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-72'} p-4 flex flex-col sticky left-0 top-0`}>
      <div className="flex-grow">
        <div className="sticky top-0">
          <div className="flex items-center mb-6 relative" ref={orgDropdownRef}>
            {!collapsed && (
              <>
                <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center">
                  <BsGrid className="w-5 h-5 text-gray-700" />
                </div>
                {isFeatureEnabled('ORGANIZATION_DROPDOWN') ? (
                  <>
                    <button 
                      onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                      className="flex items-center gap-2 text-[15px] font-normal text-gray-900 ml-3 hover:text-gray-700 transition-all"
                      style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                    >
                      Mirapath, Inc.
                      <IoChevronDownOutline className={`w-3 h-3 transition-transform ${showOrgDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showOrgDropdown && (
                      <div className="absolute top-8 left-0 mt-2 w-72 bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[rgba(112,115,147,0.16)] py-2 z-50">
                        <button
                          onClick={() => {
                            // Handle Sunbird selection
                            setShowOrgDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-[#F0F1F4] text-gray-700 flex items-center gap-3 text-[14px] transition-all"
                          style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                        >
                          <BsBuilding className="w-3 h-3 text-gray-700" />
                          <span>Sunbird, Inc. <span className={getBadgeTailwindClasses('dev')}>dev</span></span>
                        </button>
                        <button
                          onClick={() => {
                            // Handle Create Organisation
                            setShowOrgDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-[#F0F1F4] text-gray-700 flex items-center gap-3 text-[14px] transition-all"
                          style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                        >
                          <AiOutlinePlus className="w-3 h-3 text-gray-700" />
                          <span>Create Organisation <span className={getBadgeTailwindClasses('dev')}>dev</span></span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <span 
                    className="text-[15px] font-normal text-gray-900 ml-3"
                    style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                  >
                    Finderz AI
                  </span>
                )}
              </>
            )}
            <button 
              onClick={() => {
                localStorage.setItem('collapsed', String(!collapsed));
                setCollapsed(!collapsed);
              }} 
              className={`text-gray-400 hover:text-gray-600 transition-colors ${collapsed ? 'mx-auto' : 'ml-auto'}`}
            >
              {collapsed ? <LuPanelLeft className="w-5 h-5" /> : <LuPanelLeftClose className="w-5 h-5" />}
            </button>
          </div>



          <nav>
            <ul className="space-y-1">
              {extendedNav.map((n, idx) => (
                <React.Fragment key={n.link + n.label + idx}>
                  <li>
                    {n.link ? (
                      <NavLink 
                        to={n.link} 
                        className={({ isActive }) => 
                          `flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-2 text-[14px] rounded-xl transition-all
                          ${isActive 
                            ? 'text-gray-900 font-normal bg-[#F0F1F4]' 
                            : 'text-gray-500 hover:text-gray-900 hover:bg-[#F0F1F4]'}`
                        }
                        style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                        title={collapsed ? n.label : undefined}
                      >
                        <div className="flex items-center gap-3">
                        {n.icon && <span className="text-lg">{n.icon}</span>}
                        {!collapsed && n.label}
                        </div>
                        <div className="flex items-center gap-2">
                          {!collapsed && n.taskCount !== undefined && (
                            <span className="text-xs font-normal px-2 py-0.5 rounded-full min-w-[20px] text-center text-gray-900" style={{ backgroundColor: 'rgba(82, 102, 235, 0.1)', fontFamily: 'Circe, system-ui, sans-serif' }}>
                              {n.taskCount}
                            </span>
                          )}
                          {!collapsed && n.projectCount !== undefined && (
                            <span className="text-xs font-normal px-2 py-0.5 rounded-full min-w-[20px] text-center text-gray-900" style={{ backgroundColor: 'rgba(82, 102, 235, 0.1)', fontFamily: 'Circe, system-ui, sans-serif' }}>
                              {n.projectCount}
                            </span>
                          )}
                          {!collapsed && n.label === 'Projects' && (
                            <div className="relative" ref={projectSortRef}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowProjectSortDropdown(!showProjectSortDropdown);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                title="Sort projects"
                              >
                                {currentSortOption.icon}
                              </button>
                              {showProjectSortDropdown && (
                                <div className="absolute top-8 right-0 mt-1 w-56 bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[rgba(112,115,147,0.16)] py-2 z-50">
                                  {projectSortOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      onClick={() => {
                                        setProjectSortBy(option.value);
                                        setShowProjectSortDropdown(false);
                                      }}
                                      className={`w-full px-4 py-2 text-left hover:bg-[#F0F1F4] text-gray-700 flex items-center gap-3 text-[13px] transition-all ${
                                        projectSortBy === option.value ? 'bg-[#F0F1F4] font-medium' : ''
                                      }`}
                                      style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                                    >
                                      {option.icon}
                                      <span>{option.label}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </NavLink>
                    ) : (
                      <div 
                        className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-[14px] text-gray-500`}
                        style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                        title={collapsed ? n.label : undefined}
                      >
                        {n.icon && <span className="text-lg">{n.icon}</span>}
                        {!collapsed && n.label}
                      </div>
                    )}
                  </li>
                  {!collapsed && n.children && (
                    <ul className="ml-8 space-y-1 mt-1">
                      {n.label === 'Projects' && n.children.length === 0 ? (
                        <li>
                          <button
                            onClick={() => setShowNewProjectModal(true)}
                            className="flex items-center gap-2 px-3 py-2 text-[13px] text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200 w-full text-left"
                            style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                          >
                            <IoAddCircleOutline className="w-4 h-4" />
                            <span>Add Project</span>
                          </button>
                        </li>
                      ) : n.children.map((c:NavInterface) => (
                        <React.Fragment key={c.link + c.label}>
                          {c.label !== 'Default' && (
                            <li>
                              <NavLink
                                to={c.link || ''}
                                className={({ isActive }) =>
                                  `flex items-center justify-between px-3 py-2 text-[13px] rounded-md transition-colors duration-200
                                  ${isActive 
                                    ? 'text-gray-900 font-normal bg-gray-50' 
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                                }
                                style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                              >
                                <span>{c.label}</span>
                                <div className="flex items-center gap-2">
                                  {('issueCount' in c) && typeof c.issueCount === 'number' && !c.children && (
                                    <div className={`w-1.5 h-1.5 rounded-full ${getIssueColor(c.issueCount)}`} />
                                  )}
                                  {c.children && (
                                    <IoChevronDownOutline className={`w-3 h-3 transition-transform ${c.children ? 'transform rotate-0' : 'rotate-180'}`} />
                                  )}
                                </div>
                              </NavLink>
                              {c.children && (
                                <ul className="ml-4 space-y-1 mt-1">
                                  {c.children.map((cc:NavInterface, idx) => (
                                    <React.Fragment key={cc.link + cc.label + idx}>
                                      {cc.label !== 'Default' && (
                                        <li>
                                          {cc.type === 'string' ? (
                                            <div className="flex items-center justify-between px-3 py-1.5 text-[12px] text-gray-400 font-light" style={{ fontFamily: 'Circe, system-ui, sans-serif' }}>
                                              <span>{cc.label}</span>
                                              <div className={`w-1.5 h-1.5 rounded-full ${getIssueColor(getRandomIssueCount())}`} />
                                            </div>
                                          ) : (
                                            <NavLink
                                              to={cc.link || ''}
                                              className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-1.5 ml-2 text-[12px] rounded-md transition-colors duration-200
                                                ${isActive 
                                                  ? 'text-gray-900 font-normal bg-gray-50' 
                                                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                                              }
                                              style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                                            >
                                              <div className={`rounded-full flex-shrink-0 ${getIssueColor(getRandomIssueCount())}`} style={{ width: '2px', height: '2px' }} />
                                              <span>{cc.label}</span>
                                            </NavLink>
                                          )}
                                        </li>
                                      )}
                                    </React.Fragment>
                                  ))}
                                  <li>
                                    <button
                                      onClick={() => {
                                        setCreateRoomModalProps({
                                          projectId: projectId,
                                          siteId: currentSiteId
                                        });
                                        setShowCreateRoomModal(true);
                                      }}
                                      className="w-full px-3 py-1.5 text-[12px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors duration-200 text-left rounded-md font-medium flex items-center gap-2"
                                      style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                                    >
                                      <AiOutlinePlus className="w-3 h-3" />
                                      Add Room
                                    </button>
                                  </li>
                                </ul>
                              )}
                            </li>
                          )}
                        </React.Fragment>
                      ))}
                    </ul>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {!collapsed && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className='text-xs text-gray-400 font-light space-y-1' style={{ fontFamily: 'Circe, system-ui, sans-serif' }}>
            <div>
              {versionInfo.version ? `v${versionInfo.version}` : ''}
              {versionInfo.build ? ` Build ${versionInfo.build}` : 'Build 825'}
            </div>
            <div>
              Last Update: {format(
                versionInfo.build_date 
                  ? new Date(versionInfo.build_date) 
                  : new Date(), 
                'MMM d, yyyy HH:mm'
              )}
            </div>
          </div>
        </div>
      )}


    </aside>
    
    {showNewProjectModal && (
      <NewProjectForm 
        onClose={() => setShowNewProjectModal(false)} 
        onProjectCreated={async (projectId: string) => {
          // Navigate to the new project
          navigate(`/projects/${projectId}`);
          setShowNewProjectModal(false);
          // Reload projects
          await loadProjectsPage(1);
        }}
      />
    )}
    </>
  );
};
